import {
  APPLICATION_CHECK_STATUS,
  APPLICATION_CLARIFICATION_STATUS,
  APPLICATION_STATUS,
} from '../../../../../config/constants'

import './client-check-item-blocks.scss'
import { ExpandMore, FilePresent, Notification } from '@vedmarket/ui-kit'
import { applicationNotificationType, statusHintKey } from '../detail-visibility'
import { useId } from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material'
import {
  COLORS,
} from 'config/constants'

import {
  activeCheckItemKind,
  checkItemBlockNeedsClientAttention,
  clientBlockHasActionContent,
  clientCheckItemsForDisplay,
  clarificationsForCheckKind,
  showClientCheckBlocksSection,
  showApplicationPreparedDocumentsZipDownload,
  showClientCheckItemDiscussionComposer,
} from '../check-block-utils'
import CheckItemBlockCard from './check-item-block-card'
import { checkItemDiscussionHasContent } from './check-item-discussion-content'
import CheckItemDiscussion from './check-item-discussion'
import PreparedDocumentsZipButton from './prepared-documents-zip-button'
import TnvedCheckProductsPanel from './tnved-check-products-panel'

const ClientCheckItemBlocks = ({
  app,
  publicId,
  checkStatuses,
  runAction,
  onPostCheckItemComment,
  onConfirmTnvedChanges,
  isManager = false,
}: ApplicationClientCheckItemBlocksProps) => {
  const { t } = useTranslation()
  const accordionId = useId()

  if (!showClientCheckBlocksSection(app)) {
    return null
  }

  const items = clientCheckItemsForDisplay(app)
  const currentBlockKind = activeCheckItemKind(items)
  const hintKey = statusHintKey({ status: app.status, managerView: isManager })
  const hideClosedHint = !isManager && app.status === APPLICATION_STATUS.CLOSED
  const hasHint = !hideClosedHint && t(hintKey) !== hintKey
  const awaitingExecutor = app.status === APPLICATION_STATUS.AWAITING_EXECUTOR_PROPOSAL
  const hasTransferRequest = !isManager && Boolean(app.executor_transfer_requested_at)
  const showTransferConfirmation = awaitingExecutor && hasTransferRequest
  const hintText = showTransferConfirmation
    ? `${t('applications__text__transferRequested')}\n\n${t(hintKey)}`
    : t(hintKey)
  const showZipDownload = showApplicationPreparedDocumentsZipDownload({ app, isManager })
  const hasContentBeforeBlocks = hasHint || showZipDownload

  const content = (
    <>
      {hasHint && (
        <Notification
          key={app.status}
          type={applicationNotificationType(app)}
          noCloseButton
          sx={{
            marginBottom: 0,
          }}
          text={(
            <Typography variant='body2' sx={{
              color: 'inherit',
              whiteSpace: 'pre-line',
            }}>
              {hintText}
            </Typography>
          )}
        />
      )}
      {showZipDownload && <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '16px',
      }}>
        <PreparedDocumentsZipButton app={app} publicId={publicId} isManager={isManager} />
      </Box>}
      <Box className='application-client-check-item-blocks__check-blocks' sx={{
        marginTop: hasContentBeforeBlocks ? '24px' : 0,
      }}>
        {items.map((item) => {
          const clarifications = clarificationsForCheckKind({ clarifications: app.clarifications, kind: item.kind, kindLabel: item.kind_label })
          const openClarification = clarifications.find((c) => c.status === APPLICATION_CLARIFICATION_STATUS.OPEN)
          const hasActionContent = clientBlockHasActionContent({ app: app, item: item })
          const managerNote = (item.manager_note || '').trim()
          const hasOpenClarificationNote = Boolean(openClarification && managerNote)
          const noteDuplicatesOpenClar = hasOpenClarificationNote
        && managerNote === openClarification?.question.trim()

          const preparedDocuments = item.prepared_documents ?? []
          const hasPreparedDocuments = preparedDocuments.length > 0
          const hasNoActionContent = !hasActionContent && !hasPreparedDocuments
          const isCompleted = item.status === APPLICATION_CHECK_STATUS.COMPLETED
          const showWaitingHint = hasNoActionContent && !isCompleted

          const showManagerNote = !isCompleted && Boolean(managerNote) && !noteDuplicatesOpenClar
          const hasTnvedReviews = item.kind === 'tnved_code' && (item.tnved_reviews?.length ?? 0) > 0
          const showTnvedPanel = hasTnvedReviews && Boolean(app.calculation_number)
          const showComposer = showClientCheckItemDiscussionComposer(app, item)
          const hasDiscussion = checkItemDiscussionHasContent({ app, item, showComposer })
          const hasNoteOrHint = showWaitingHint || showManagerNote
          const hasPanelOrDiscussion = showTnvedPanel || hasDiscussion
          const hasContent = hasNoteOrHint || hasPanelOrDiscussion

          return (
            <CheckItemBlockCard
              key={item.kind}
              managerView={isManager}
              item={item}
              hasContent={hasContent}
              isActiveBlock={item.kind === currentBlockKind}
              needsClientAttention={checkItemBlockNeedsClientAttention({ app: app, item: item })}
            >
              {showWaitingHint && (
                <Typography variant='body2' sx={{ color: isManager ? 'text.primary' : COLORS.LIGHT_TEXT_SECONDARY }}>
                  {t('applications__text__blockClientWaiting')}
                </Typography>
              )}

              {showManagerNote && (
                <Typography color={isManager ? 'text.primary' : undefined} variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                  {managerNote}
                </Typography>
              )}

              {showTnvedPanel && (
                <TnvedCheckProductsPanel
                  orderId={app.calculation_number}
                  managerView={isManager}
                  reviews={item.tnved_reviews}
                  changesConfirmed={Boolean(item.tnved_changes_confirmed)}
                  readOnly
                  runAction={runAction}
                  onConfirmChanges={!isManager && !isCompleted
                    ? () => onConfirmTnvedChanges()
                    : undefined}
                />
              )}

              <CheckItemDiscussion
                managerView={isManager}
                app={app}
                item={item}
                showEvents={false}
                checkStatuses={checkStatuses}
                showComposer={showComposer}
                runAction={runAction}
                onPostComment={onPostCheckItemComment}
              />
            </CheckItemBlockCard>
          )
        })}
      </Box>
    </>
  )

  return (
    <section id='application-package' className='application-client-check-item-blocks'>
      {isManager ? content : (
        <Accordion
          defaultExpanded
          sx={{ boxShadow: 'none' }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore width={32} height={32} />}
            id={`${accordionId}-summary`}
            aria-controls={`${accordionId}-content`}
            sx={{ padding: '12px 24px' }}
          >
            <Typography
              variant='h5'
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                fontWeight: 700,
              }}
            >
              <FilePresent
                width={32}
                height={32}
                style={{
                  color: COLORS.LIGHT_TEXT_DISABLED_OPACITY,
                  flexShrink: 0,
                }}
              />
              {t('applications__text__checkItems')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>{content}</AccordionDetails>
        </Accordion>
      )}
    </section>
  )
}

export default ClientCheckItemBlocks
